import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useGateway from '../../hooks/useGateway';
import { nfmt } from '../../lib/formatters';

const Spin = () => (
  <div className="empty">
    <div className="spin" />
    <span>Loading…</span>
  </div>
);

const Fail = ({ error }) => (
  <div className="empty" style={{ color: 'var(--red)' }}>
    <b>Request failed</b>
    {error?.message || String(error)}
  </div>
);

function CountriesTab() {
  const { data, loading, error } = useGateway('lyveGeographies', { kind: 'countries', countryHasUpcomingEvents: true });
  if (loading) return <Spin />;
  if (error) return <Fail error={error} />;
  const list = data?.countries || [];
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Country</th>
          <th>Code</th>
          <th>Upcoming events</th>
        </tr>
      </thead>
      <tbody>
        {list.map((c) => (
          <tr key={c.identifier}>
            <td>
              <Link to={`/events-catalog?country=${encodeURIComponent(c.identifier)}`}>{c.name}</Link>
            </td>
            <td>{c.identifier}</td>
            <td>{nfmt(c['x-numUpcomingEvents'])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatesTab() {
  const { data, loading, error } = useGateway('lyveGeographies', { kind: 'states', stateHasUpcomingEvents: true });
  if (loading) return <Spin />;
  if (error) return <Fail error={error} />;
  const list = data?.states || [];
  return (
    <table className="table">
      <thead>
        <tr>
          <th>State / region</th>
          <th>ISO</th>
          <th>Country</th>
          <th>Upcoming events</th>
        </tr>
      </thead>
      <tbody>
        {list.map((s) => (
          <tr key={s.identifier}>
            <td>
              <Link to={`/events-catalog?state=${encodeURIComponent(s.identifier)}`}>{s.name}</Link>
            </td>
            <td>{s.identifier}</td>
            <td>{s.country?.name || ''}</td>
            <td>{nfmt(s['x-numUpcomingEvents'])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MetrosTab() {
  const { data, loading, error } = useGateway('lyveGeographies', {
    kind: 'metros',
    metroHasUpcomingEvents: true,
    expandMetroCities: true,
  });
  if (loading) return <Spin />;
  if (error) return <Fail error={error} />;
  const list = data?.metros || [];
  const sorted = [...list].sort((a, b) => (b['x-numUpcomingEvents'] || 0) - (a['x-numUpcomingEvents'] || 0));
  return (
    <div className="rp-grid rp-cols-2">
      {sorted.map((m) => (
        <div className="geo-card" key={m.identifier}>
          <h3>
            <Link to={`/events-catalog?metro=${encodeURIComponent(m.identifier)}`}>{m.name}</Link>
          </h3>
          <div className="sub">
            {m.address?.addressRegion?.name || ''} · {nfmt(m['x-numUpcomingEvents'])} upcoming events
          </div>
          {m.containsPlace?.length > 0 && (
            <div className="rchips" style={{ marginTop: 10 }}>
              {m.containsPlace.map((c) => (
                <Link key={c.identifier} className="rchip" to={`/events-catalog?city=${encodeURIComponent(c.name)}`}>
                  {c.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function CitiesTab() {
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const { data, loading, error } = useGateway(
    'lyveGeographies',
    query ? { kind: 'cities', geoCityName: query } : { kind: 'cities', geoCountryIso2: 'US', cityHasUpcomingEvents: true }
  );

  const submit = (e) => {
    e.preventDefault();
    setQuery(name.trim());
  };

  const cities = data?.cities || [];

  return (
    <div>
      <form className="acat-bar" onSubmit={submit}>
        <input
          className="acat-input"
          placeholder="Search cities (e.g. Brooklyn)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button className="acat-go" type="submit">
          Search
        </button>
        <button
          className="rp-back"
          type="button"
          onClick={() => {
            setName('');
            setQuery('');
          }}>
          Show cities with events
        </button>
      </form>

      {loading && <Spin />}
      {error && <Fail error={error} />}
      {!loading && !error && cities.length === 0 && (
        <div className="empty">
          <b>No cities found.</b>
        </div>
      )}
      {!loading && !error && cities.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>City</th>
              <th>State</th>
              <th>Country</th>
              <th>Metro</th>
              <th>Upcoming</th>
            </tr>
          </thead>
          <tbody>
            {cities.map((c) => (
              <tr key={c.identifier}>
                <td>
                  <Link to={`/events-catalog?city=${encodeURIComponent(c.name)}`}>{c.name}</Link>
                </td>
                <td>{c.address?.addressRegion?.alternateName || '—'}</td>
                <td>{c.address?.addressCountry?.alternateName || '—'}</td>
                <td>{c.containedInPlace?.name || '—'}</td>
                <td>{nfmt(c['x-numUpcomingEvents'])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Geographies() {
  const [tab, setTab] = useState('metros');
  return (
    <div className="lyve-intel">
      <div className="hero" style={{ paddingBottom: 0 }}>
        <div className="eyebrow">
          <i />
          DISCOVER
        </div>
        <h1 className="h-title wide">GEOGRAPHIES</h1>
        <p className="h-sub">
          Countries, states, metro areas and cities — each linked to its live event calendar. Click through to explore
          events.
        </p>
      </div>

      <div className="page" style={{ paddingTop: 18 }}>
        <div className="ftabs">
          <button className="ftab" aria-pressed={tab === 'metros'} onClick={() => setTab('metros')}>
            Metros
          </button>
          <button className="ftab" aria-pressed={tab === 'cities'} onClick={() => setTab('cities')}>
            Cities
          </button>
          <button className="ftab" aria-pressed={tab === 'states'} onClick={() => setTab('states')}>
            States
          </button>
          <button className="ftab" aria-pressed={tab === 'countries'} onClick={() => setTab('countries')}>
            Countries
          </button>
        </div>

        {tab === 'metros' && <MetrosTab />}
        {tab === 'cities' && <CitiesTab />}
        {tab === 'states' && <StatesTab />}
        {tab === 'countries' && <CountriesTab />}
      </div>
    </div>
  );
}
